import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { usePageTitle } from "@/lib/useTitle";
import { formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { t } = useLang();
  usePageTitle(t("title.login"));
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.welcome"));
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader />
      <div className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md animate-fade-up">
          <div className="text-center mb-8">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-ink mb-4">
              <Lock className="h-5 w-5 text-sage-light" strokeWidth={2} />
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl text-ink">{t("login.title")}</h1>
            <p className="text-gray-600 text-sm mt-2">{t("login.subtitle")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-border rounded-sm p-7">
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.15em] text-gray-600 font-semibold">{t("login.email")}</Label>
              <Input type="email" required data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@email.com" className="h-12 rounded-sm border-border focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.15em] text-gray-600 font-semibold">{t("login.password")}</Label>
              <Input type="password" required data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 rounded-sm border-border focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-12 rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold">
              {loading ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          <button onClick={() => navigate("/")} data-testid="login-back-link" className="mt-6 mx-auto flex items-center gap-2 text-sm text-gray-400 hover:text-ink transition-colors duration-200 font-semibold">
            <ArrowLeft className="h-4 w-4" /> {t("login.back")}
          </button>
        </div>
      </div>
    </div>
  );
}
